import React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { Button } from 'dibs-elements/exports/Button';
import { Spinner } from 'dibs-elements/exports/Spinner';

// Components
import CartToolbar from './CartToolbar';
import CartSupport from './CartSupport';
import CartItems from './CartItems';
import { CartEmptyWrapper } from './CartEmptyWrapper';
import AccountUserVip from '../account/accountManager/AccountUserVip';

import styles from './styles/CartLayout.scss';

const CartLayout = props => {
    const { viewer, portfolioItems, localItems, user, isLoadingMore, onLoadMore } = props;
    const totalResults = user
        ? get(portfolioItems, 'totalResults')
        : get(localItems, 'totalResults');
    const firstItemCategoryPath = get(portfolioItems, 'edges[0].node.item.categoryPath');
    const isVerifiedTrade = get(user, 'isVerifiedTrade');
    const showVipSalesRep = !isVerifiedTrade && get(viewer, 'userRelationships.length') > 0;
    const hasNextPage = get(portfolioItems, 'pageInfo.hasNextPage');
    const hasVipSalesRepName = !!get(viewer, 'userRelationships[0].userDataB[0].displayName');

    return (
        <div>
            <CartToolbar
                totalResults={totalResults}
                firstItemCategoryPath={firstItemCategoryPath}
            />

            {totalResults > 0 ? (
                <CartItems
                    user={user}
                    viewer={viewer}
                    portfolioItems={portfolioItems}
                    localItems={localItems}
                />
            ) : (
                <CartEmptyWrapper hasUserId={!!user} />
            )}

            {isLoadingMore && (
                <div className={styles.loadMoreWrapper}>
                    <Spinner />
                </div>
            )}

            {hasNextPage && !isLoadingMore && (
                <div className={styles.loadMoreWrapper}>
                    <Button onClick={onLoadMore} dataTn="cart-load-more-items">
                        <FormattedMessage id="CartLayout.loadMore" defaultMessage="Load More" />
                    </Button>
                </div>
            )}

            {showVipSalesRep ? (
                <AccountUserVip
                    userRelationships={viewer.userRelationships}
                    header={
                        <FormattedMessage
                            id="CartLayout.vipHeader"
                            defaultMessage="Questions? Contact
                                            {hasName, select,
                                                true { Your }
                                                false { a }
                                            }Private Client Specialist"
                            values={{ hasName: hasVipSalesRepName }}
                        />
                    }
                />
            ) : (
                <CartSupport />
            )}
        </div>
    );
};

CartLayout.propTypes = {
    user: PropTypes.object,
    viewer: PropTypes.object.isRequired,
    portfolioItems: PropTypes.object,
    localItems: PropTypes.object,
    onLoadMore: PropTypes.func,
    isLoadingMore: PropTypes.bool,
};

export default createFragmentContainer(CartLayout, {
    viewer: graphql`
        fragment CartLayout_viewer on Viewer {
            ...CartItems_viewer
            userRelationships(userIds: $userIds, relationshipType: VIP) @include(if: $hasUserId) {
                userDataB {
                    ... on VipRepData {
                        displayName
                        phoneNumber(formatType: INTERNATIONAL)
                        email
                    }
                }
            }
        }
    `,
    user: graphql`
        fragment CartLayout_user on User {
            ...CartItems_user
            isVerifiedTrade
        }
    `,
    portfolioItems: graphql`
        fragment CartLayout_portfolioItems on PortfolioItemsConnection {
            ...CartItems_portfolioItems
            totalResults
            pageInfo {
                hasNextPage
            }
            edges {
                node {
                    item {
                        categoryPath
                    }
                }
            }
        }
    `,
    localItems: graphql`
        fragment CartLayout_localItems on ItemsConnection {
            ...CartItems_localItems
            totalResults
        }
    `,
});
